/**
 * <TierFormDialog> — create + edit form for subscription tiers.
 *
 * signup-billing-mvp Phase 5 Session 5 (2026-05-13).
 *
 * Wraps the existing `<ConfirmModal>` primitive (blueprint L978) — the
 * children slot carries the form fields. Modes: `'create'` | `'edit'`.
 *
 * Stripe-ID fields are NOT inputs — they're read-only displays of the
 * existing IDs in edit mode (truncated). System-managed per spec § 5e.
 *
 * Live-computed derived-grant preview:
 *   derived = base_tier.monthly_price_usd × usage_multiplier × base_ratio
 * Reads `pricing_config` + admin tier list once on mount; recomputes as the
 * admin types `monthly_price_usd` + `usage_multiplier`.
 *
 * Optimistic concurrency: edit mode submits `version` from the loaded row;
 * 409 OPTIMISTIC_LOCK_FAILED surfaces the error AND refreshes the dialog
 * state from `details.current_row` so a re-submit uses the fresh version.
 */
import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { ConfirmModal } from '@/components/ui';
import { ApiError, api } from '@/lib/api';
import { queryKeys } from '@/lib/query-keys';
import {
  tierCreateRequestSchema,
  tierUpdateRequestSchema,
} from '@arcanum-hq/shared-types';

interface TierRow {
  id: string;
  display_name: string;
  tagline: string;
  monthly_price_usd: number | null;
  usage_multiplier: number;
  feature_bullets: string[];
  sort_order: number;
  is_active: boolean;
  version: number;
  stripe_product_id: string | null;
  stripe_price_id: string | null;
  derived_monthly_grant_cogs: number;
}

interface PricingConfigRow {
  base_ratio_subscription_to_cogs: number;
}

interface CreateProps {
  mode: 'create';
  onClose: () => void;
  onSaved: () => void;
}

interface EditProps {
  mode: 'edit';
  tier: TierRow;
  onClose: () => void;
  onSaved: () => void;
}

type TierFormDialogProps = CreateProps | EditProps;

// Defaults for the Phase-2 seed: `pricing_config.base_ratio_subscription_to_cogs = 20`.
const DEFAULT_BASE_RATIO = 20;

function truncateStripeId(id: string | null): string {
  if (!id) return '—';
  if (id.length <= 24) return id;
  return `${id.slice(0, 18)}…${id.slice(-3)}`;
}

export function TierFormDialog(props: TierFormDialogProps) {
  const isEdit = props.mode === 'edit';
  const initial = isEdit ? props.tier : null;

  const [id, setId] = useState(initial?.id ?? '');
  const [displayName, setDisplayName] = useState(initial?.display_name ?? '');
  const [tagline, setTagline] = useState(initial?.tagline ?? '');
  const [contactUs, setContactUs] = useState(initial?.monthly_price_usd === null);
  const [priceUsd, setPriceUsd] = useState<string>(
    initial?.monthly_price_usd !== null && initial?.monthly_price_usd !== undefined
      ? String(initial.monthly_price_usd)
      : '',
  );
  const [usageMultiplier, setUsageMultiplier] = useState<string>(
    initial?.usage_multiplier !== undefined ? String(initial.usage_multiplier) : '1',
  );
  const [featureBullets, setFeatureBullets] = useState(
    (initial?.feature_bullets ?? []).join('\n'),
  );
  const [sortOrder, setSortOrder] = useState<string>(
    initial?.sort_order !== undefined ? String(initial.sort_order) : '0',
  );
  const [isActive, setIsActive] = useState(initial?.is_active ?? true);
  // Optimistic-concurrency token — updates when 409 refreshes from current_row.
  const [version, setVersion] = useState(initial?.version ?? 0);
  // Read-only Stripe IDs (display only in edit mode).
  const [stripeProductId, setStripeProductId] = useState(initial?.stripe_product_id ?? null);
  const [stripePriceId, setStripePriceId] = useState(initial?.stripe_price_id ?? null);

  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Fetch pricing config + admin tier list (the base tier defines the grant).
  const pricingQuery = useQuery<PricingConfigRow>({
    queryKey: queryKeys.billing.pricingConfig(),
    queryFn: async () => {
      try {
        const env = await api.get<{ data: PricingConfigRow }>(
          '/api/billing/admin/pricing-config',
        );
        return env.data;
      } catch {
        // Phase 6 ships the route; degrade gracefully to default ratio.
        return { base_ratio_subscription_to_cogs: DEFAULT_BASE_RATIO };
      }
    },
    staleTime: 60_000,
  });

  const tiersQuery = useQuery<TierRow[]>({
    queryKey: queryKeys.billing.adminTiers(),
    queryFn: async () => {
      const env = await api.get<{ data: TierRow[] }>('/api/billing/admin/tiers');
      return env.data;
    },
    staleTime: 30_000,
  });

  // Live-computed derived grant preview.
  // Formula re-locked v2.12.5 (2026-05-14): `monthly_grant_cogs =
  // base_price_usd × usage_multiplier` — NO `× base_ratio`. See root
  // CLAUDE.md COGS-vs-dollars footgun #15. The per-event ratio conversion
  // happens in `record_usage_event` (DB RPC) at debit time, not here at
  // grant derivation time. Multiplying here would give a 20× over-
  // allowance (Principal 499 × 1.0 × 20 = 9,980 → $499 of Anthropic;
  // correct is 499 COGS = $24.95 Anthropic).
  const derivedPreview = useMemo(() => {
    if (contactUs) return null;
    const mult = parseFloat(usageMultiplier);
    const baseTier = (tiersQuery.data ?? []).find((t) => t.usage_multiplier === 1);
    const basePriceUsd = baseTier?.monthly_price_usd ?? null;
    if (basePriceUsd === null || !Number.isFinite(mult) || mult <= 0) return null;
    const grantCogs = Math.round(basePriceUsd * mult);
    const baseRatio = pricingQuery.data?.base_ratio_subscription_to_cogs ?? DEFAULT_BASE_RATIO;
    const anthropicUsd = baseRatio > 0 ? grantCogs / baseRatio : 0;
    return { grantCogs, anthropicUsd, baseRatio };
  }, [usageMultiplier, contactUs, pricingQuery.data, tiersQuery.data]);

  function buildBody(): {
    body: Record<string, unknown>;
    errors: string[];
  } {
    const errors: string[] = [];
    const parsedPrice = contactUs ? null : parseFloat(priceUsd);
    const parsedMult = parseFloat(usageMultiplier);
    const parsedSort = parseInt(sortOrder, 10);
    const bullets = featureBullets
      .split('\n')
      .map((s) => s.trim())
      .filter(Boolean);

    if (!contactUs && (!Number.isFinite(parsedPrice as number) || (parsedPrice as number) < 0)) {
      errors.push('Monthly price must be a non-negative number (or toggle "Contact us").');
    }
    if (!Number.isFinite(parsedMult) || parsedMult <= 0) {
      errors.push('Usage multiplier must be > 0.');
    }
    if (!Number.isFinite(parsedSort)) {
      errors.push('Sort order must be an integer.');
    }

    const body: Record<string, unknown> = {
      id: id.trim(),
      display_name: displayName.trim(),
      tagline: tagline.trim(),
      monthly_price_usd: contactUs ? null : (parsedPrice as number),
      usage_multiplier: parsedMult,
      feature_bullets: bullets,
      sort_order: parsedSort,
      is_active: isActive,
    };

    const schema = isEdit ? tierUpdateRequestSchema : tierCreateRequestSchema;
    if (isEdit) {
      body.version = version;
    }
    const parsed = schema.safeParse(body);
    if (!parsed.success) {
      for (const issue of parsed.error.issues) {
        errors.push(`${issue.path.join('.') || '(root)'}: ${issue.message}`);
      }
    }
    return { body, errors };
  }

  async function handleSubmit() {
    setError(null);
    const { body, errors } = buildBody();
    if (errors.length > 0) {
      setError(errors.join(' · '));
      return;
    }
    setSubmitting(true);
    try {
      if (isEdit) {
        await api.put(`/api/billing/admin/tiers/${id}`, body);
      } else {
        await api.post('/api/billing/admin/tiers', body);
      }
      props.onSaved();
    } catch (e) {
      if (e instanceof ApiError && e.status === 409 && e.code === 'OPTIMISTIC_LOCK_FAILED') {
        const currentRow = (e.details?.['current_row'] ?? null) as TierRow | null;
        if (currentRow) {
          // Refresh dialog state from server's current_row.
          setDisplayName(currentRow.display_name);
          setTagline(currentRow.tagline);
          setContactUs(currentRow.monthly_price_usd === null);
          setPriceUsd(
            currentRow.monthly_price_usd !== null
              ? String(currentRow.monthly_price_usd)
              : '',
          );
          setUsageMultiplier(String(currentRow.usage_multiplier));
          setFeatureBullets(currentRow.feature_bullets.join('\n'));
          setSortOrder(String(currentRow.sort_order));
          setIsActive(currentRow.is_active);
          setVersion(currentRow.version);
          setStripeProductId(currentRow.stripe_product_id);
          setStripePriceId(currentRow.stripe_price_id);
        }
        setError(
          'Tier was edited by another admin. The form has been refreshed — review and re-submit.',
        );
      } else if (e instanceof ApiError) {
        setError(`${e.code ?? 'ERROR'}: ${e.message}`);
      } else if (e instanceof Error) {
        setError(e.message);
      } else {
        setError('Unknown error');
      }
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <ConfirmModal
      open={true}
      onClose={props.onClose}
      onConfirm={handleSubmit}
      title={isEdit ? `Edit Tier — ${initial?.id ?? ''}` : 'New Tier'}
      confirmLabel={submitting ? 'Saving…' : isEdit ? 'Save changes' : 'Create tier'}
    >
      <div className="flex flex-col gap-3" data-testid="tier-form-dialog">
        {/* ID — disabled in edit mode */}
        <label className="flex flex-col gap-1">
          <span className="text-xs font-mono text-text-muted tracking-[1.1px] uppercase">
            Tier ID (slug)
          </span>
          <input
            type="text"
            value={id}
            onChange={(e) => setId(e.target.value)}
            disabled={isEdit}
            placeholder="e.g. principal"
            className="px-3 py-2 bg-input-bg border border-input-border rounded-[2px] text-text-bright text-sm font-mono outline-none focus:border-accent/20 disabled:opacity-50"
            data-testid="tier-form-id"
          />
        </label>

        <label className="flex flex-col gap-1">
          <span className="text-xs font-mono text-text-muted tracking-[1.1px] uppercase">
            Display name
          </span>
          <input
            type="text"
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
            className="px-3 py-2 bg-input-bg border border-input-border rounded-[2px] text-text-bright text-sm font-body outline-none focus:border-accent/20"
            data-testid="tier-form-display-name"
          />
        </label>

        <label className="flex flex-col gap-1">
          <span className="text-xs font-mono text-text-muted tracking-[1.1px] uppercase">
            Tagline
          </span>
          <input
            type="text"
            value={tagline}
            onChange={(e) => setTagline(e.target.value)}
            className="px-3 py-2 bg-input-bg border border-input-border rounded-[2px] text-text-bright text-sm font-body outline-none focus:border-accent/20"
            data-testid="tier-form-tagline"
          />
        </label>

        {/* Contact-us toggle gates the price field */}
        <label className="flex items-center gap-2">
          <input
            type="checkbox"
            checked={contactUs}
            onChange={(e) => setContactUs(e.target.checked)}
            className="accent-accent"
            data-testid="tier-form-contact-us"
          />
          <span className="text-xs font-mono text-text-muted">
            "Contact us" tier (no Stripe linkage, no recurring price)
          </span>
        </label>

        <label className="flex flex-col gap-1">
          <span className="text-xs font-mono text-text-muted tracking-[1.1px] uppercase">
            Monthly price (USD)
          </span>
          <input
            type="number"
            min={0}
            step={0.01}
            value={contactUs ? '' : priceUsd}
            onChange={(e) => setPriceUsd(e.target.value)}
            disabled={contactUs}
            className="px-3 py-2 bg-input-bg border border-input-border rounded-[2px] text-text-bright text-sm font-mono outline-none focus:border-accent/20 disabled:opacity-50"
            data-testid="tier-form-price"
          />
        </label>

        <label className="flex flex-col gap-1">
          <span className="text-xs font-mono text-text-muted tracking-[1.1px] uppercase">
            Usage multiplier
          </span>
          <input
            type="number"
            min={0.001}
            step={0.1}
            value={usageMultiplier}
            onChange={(e) => setUsageMultiplier(e.target.value)}
            className="px-3 py-2 bg-input-bg border border-input-border rounded-[2px] text-text-bright text-sm font-mono outline-none focus:border-accent/20"
            data-testid="tier-form-multiplier"
          />
          <span className="text-micro font-mono text-text-dim">
            Base tier must equal 1.0 (RPC invariant).
          </span>
        </label>

        {/* Live-computed derived grant preview */}
        <div
          className="px-3 py-2 bg-accent-dim border border-accent/15 rounded-[2px] flex flex-col gap-1"
          data-testid="tier-form-derived-preview"
        >
          <div className="flex items-center justify-between">
            <span className="text-xs font-mono text-text-muted tracking-[1.1px] uppercase">
              Derived grant
            </span>
            <span
              className="text-accent font-mono tabular-nums"
              data-testid="tier-form-derived-cogs"
            >
              {derivedPreview === null
                ? '—'
                : `${derivedPreview.grantCogs.toLocaleString('en-US')} COGS`}
            </span>
          </div>
          {derivedPreview !== null && (
            <div className="flex items-center justify-between">
              <span className="text-micro font-mono text-text-dim tracking-[1.1px] uppercase">
                ≈ Anthropic spend @ {derivedPreview.baseRatio}×
              </span>
              <span
                className="text-text-muted font-mono text-xs tabular-nums"
                data-testid="tier-form-derived-anthropic-usd"
              >
                ${derivedPreview.anthropicUsd.toLocaleString('en-US', {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                })}
              </span>
            </div>
          )}
        </div>

        <label className="flex flex-col gap-1">
          <span className="text-xs font-mono text-text-muted tracking-[1.1px] uppercase">
            Feature bullets (one per line)
          </span>
          <textarea
            value={featureBullets}
            onChange={(e) => setFeatureBullets(e.target.value)}
            rows={4}
            className="px-3 py-2 bg-input-bg border border-input-border rounded-[2px] text-text-bright text-sm font-body outline-none focus:border-accent/20 resize-y"
            data-testid="tier-form-bullets"
          />
        </label>

        <div className="grid grid-cols-2 gap-3">
          <label className="flex flex-col gap-1">
            <span className="text-xs font-mono text-text-muted tracking-[1.1px] uppercase">
              Sort order
            </span>
            <input
              type="number"
              step={1}
              value={sortOrder}
              onChange={(e) => setSortOrder(e.target.value)}
              className="px-3 py-2 bg-input-bg border border-input-border rounded-[2px] text-text-bright text-sm font-mono outline-none focus:border-accent/20"
              data-testid="tier-form-sort-order"
            />
          </label>
          <label className="flex items-end gap-2 pb-2">
            <input
              type="checkbox"
              checked={isActive}
              onChange={(e) => setIsActive(e.target.checked)}
              className="accent-accent"
              data-testid="tier-form-is-active"
            />
            <span className="text-xs font-mono text-text-muted">Active</span>
          </label>
        </div>

        {/* Read-only Stripe IDs (edit mode only) */}
        {isEdit && (
          <div className="flex flex-col gap-1 pt-2 border-t border-panel-border">
            <span className="text-micro font-mono text-text-dim uppercase tracking-[1.2px]">
              Stripe linkage (system-managed)
            </span>
            <div className="flex items-center justify-between">
              <span className="text-xs font-mono text-text-muted">Product</span>
              <code className="text-xs font-mono text-text-dim" data-testid="tier-form-stripe-product">
                {truncateStripeId(stripeProductId)}
              </code>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-xs font-mono text-text-muted">Price</span>
              <code className="text-xs font-mono text-text-dim" data-testid="tier-form-stripe-price">
                {truncateStripeId(stripePriceId)}
              </code>
            </div>
          </div>
        )}

        {error && (
          <div
            className="px-3 py-2 bg-danger/[0.06] border border-danger/15 rounded text-danger text-xs font-mono"
            data-testid="tier-form-error"
          >
            {error}
          </div>
        )}
      </div>
    </ConfirmModal>
  );
}

